<div class="predictive-modal-container">
  <div class="modal-header">
    <div class="header-left">
      <div class="icon-circle">
        <mat-icon>speed</mat-icon>
      </div>
      <div>
        <h2 class="title">{{ data?.vessel?.vessel_name }} - Predictive CII Allowance</h2>
        <span class="subtitle">
          IMO: {{ data?.vessel?.imo_number }} &bull; DWT: {{ data?.vessel?.dwt | number:'1.0-0' }} MT &bull; Type: {{ data?.vessel?.ship_type }}
        </span>
      </div>
    </div>
    <button mat-icon-button (click)="close()" class="close-btn" aria-label="Close dialog">
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <div class="modal-body" *ngIf="data">
    <!-- Timeline Banner -->
    <div class="exclusions-note">
      <mat-icon>event</mat-icon>
      <span>
        Year-To-Date ({{ data.period?.reporting_year }}):
        <strong>{{ data.period?.days_elapsed }} days elapsed</strong>,
        <strong>{{ data.period?.days_remaining }} days remaining</strong>.
        Paced projection across full annual operational cycle.
      </span>
    </div>

    <!-- Balanced Equal-Width Scenario Tabs -->
    <div class="scenario-tabs">
      <button 
        type="button" 
        class="tab-btn" 
        [class.active]="activeTab === 'distance'" 
        (click)="activeTab = 'distance'">
        <mat-icon>local_gas_station</mat-icon>
        <div>
          <span class="tab-title">Scenario 1: Distance Constant</span>
          <span class="tab-sub">Remaining Fuel Allowance to Year-End</span>
        </div>
      </button>
      <button 
        type="button" 
        class="tab-btn" 
        [class.active]="activeTab === 'fuel'" 
        (click)="activeTab = 'fuel'">
        <mat-icon>navigation</mat-icon>
        <div>
          <span class="tab-title">Scenario 2: Fuel Constant</span>
          <span class="tab-sub">Required Distance for Remaining Days</span>
        </div>
      </button>
    </div>

    <!-- Tab Content -->
    <div class="tab-view-container">
      <!-- Tab 1: Distance Constant -->
      <div class="tab-content" *ngIf="activeTab === 'distance'">
        <div class="scenario-explainer">
          <mat-icon>info</mat-icon>
          <span>
            <strong>Assumption:</strong> Vessel continues sailing at its recent pace (last 3 months) of <strong>{{ (data.scenario_fixed_distance.daily_dist_rate_3m || (data.scenario_fixed_distance.projected_remaining_distance_nm / (data.period?.days_remaining || 1))) | number:'1.0-0' }} nm/day</strong> (projected <strong>{{ data.scenario_fixed_distance.projected_remaining_distance_nm | number:'1.0-0' }} nm</strong> over remaining <strong>{{ data.period?.days_remaining }} days</strong>).
          </span>
        </div>

        <div class="metrics-grid">
          <div class="metric-card">
            <div class="card-top-row">
              <span class="metric-label">Expected Distance (for {{ data.period?.days_remaining }} days)</span>
              <span class="badge-tag badge-neutral">Paced</span>
            </div>
            <span class="metric-value">{{ data.scenario_fixed_distance.projected_remaining_distance_nm | number:'1.0-0' }} <small>nm</small></span>
            <span class="metric-sub">Average: {{ (data.scenario_fixed_distance.daily_dist_rate_3m || (data.scenario_fixed_distance.projected_remaining_distance_nm / (data.period?.days_remaining || 1))) | number:'1.0-0' }} NM/day (based on last 3 months)</span>
          </div>

          <div class="metric-card">
            <div class="card-top-row">
              <span class="metric-label">Annual Fuel Budget</span>
              <span class="badge-tag badge-info">Band C Ceiling</span>
            </div>
            <span class="metric-value">{{ data.scenario_fixed_distance.max_annual_allowed_fuel_mt | number:'1.1-1' }} <small>MT</small></span>
            <span class="metric-sub">Max allowed total fuel for 365 days (5% margin)</span>
          </div>

          <div class="metric-card card-hero" [ngClass]="data.scenario_fixed_distance.has_allowance ? 'card-success' : 'card-warning'">
            <div class="card-top-row">
              <span class="metric-label">Fuel You Can Still Burn</span>
              <span class="badge-tag" [ngClass]="data.scenario_fixed_distance.has_allowance ? 'badge-success' : 'badge-warning'">
                {{ data.scenario_fixed_distance.has_allowance ? 'Allowance' : 'Deficit' }}
              </span>
            </div>
            <span class="metric-value hero-val">
              {{ (data.scenario_fixed_distance.fuel_allowance_margin_mt > 0 ? '+' : '') }}{{ data.scenario_fixed_distance.fuel_allowance_margin_mt | number:'1.1-1' }} <small>MT</small>
            </span>
            <span class="metric-sub">
              <strong>{{ getDailyAllowanceFormatted() }}</strong> &bull; Burned: {{ data.baseline_stats.fuel_ytd | number:'1.0-0' }} MT
            </span>
          </div>
        </div>

        <div class="progress-section">
          <div class="progress-labels">
            <span>Fuel Burned So Far: <strong>{{ data.baseline_stats.fuel_ytd | number:'1.1-1' }} MT</strong> ({{ (data.baseline_stats.fuel_ytd / (data.period?.days_elapsed || 1)) | number:'1.1-1' }} MT/day)</span>
            <span>Annual Budget Limit: <strong>{{ data.scenario_fixed_distance.max_annual_allowed_fuel_mt | number:'1.1-1' }} MT</strong></span>
          </div>
          <div class="progress-bar-bg">
            <div class="progress-fill" [style.width.%]="getFuelProgressWidth()" [class.over-limit]="!data.scenario_fixed_distance.has_allowance"></div>
          </div>
        </div>

        <div class="action-alert" [ngClass]="data.scenario_fixed_distance.has_allowance ? 'alert-success' : 'alert-warning'">
          <mat-icon>{{ data.scenario_fixed_distance.has_allowance ? 'check_circle' : 'warning' }}</mat-icon>
          <p>
            <strong>Summary:</strong> Covering the projected <strong>{{ data.scenario_fixed_distance.projected_remaining_distance_nm | number:'1.0-0' }} nm</strong>, you can safely burn up to <strong>{{ data.scenario_fixed_distance.fuel_allowance_margin_mt | number:'1.1-1' }} MT</strong> (up to <strong>{{ (data.scenario_fixed_distance.fuel_allowance_margin_mt / (data.period?.days_remaining || 1)) | number:'1.1-1' }} MT/day</strong>) and still finish the year inside Band C.
          </p>
        </div>
      </div>

      <!-- Tab 2: Fuel Constant -->
      <div class="tab-content" *ngIf="activeTab === 'fuel'">
        <div class="scenario-explainer">
          <mat-icon>info</mat-icon>
          <span>
            <strong>Assumption:</strong> Vessel continues burning fuel at its recent pace (last 3 months) of <strong>{{ (data.scenario_fixed_fuel.daily_burn_rate_3m || (data.scenario_fixed_fuel.projected_remaining_fuel_mt / (data.period?.days_remaining || 1))) | number:'1.1-1' }} MT/day</strong> (projected <strong>{{ data.scenario_fixed_fuel.projected_remaining_fuel_mt | number:'1.0-1' }} MT</strong> over remaining <strong>{{ data.period?.days_remaining }} days</strong>).
          </span>
        </div>

        <div class="metrics-grid">
          <div class="metric-card">
            <div class="card-top-row">
              <span class="metric-label">Expected Fuel Burn (for {{ data.period?.days_remaining }} days)</span>
              <span class="badge-tag badge-neutral">Paced</span>
            </div>
            <span class="metric-value">{{ data.scenario_fixed_fuel.projected_remaining_fuel_mt | number:'1.1-1' }} <small>MT</small></span>
            <span class="metric-sub">Average: {{ (data.scenario_fixed_fuel.daily_burn_rate_3m || (data.scenario_fixed_fuel.projected_remaining_fuel_mt / (data.period?.days_remaining || 1))) | number:'1.1-1' }} MT/day (based on last 3 months)</span>
          </div>

          <div class="metric-card">
            <div class="card-top-row">
              <span class="metric-label">Annual Distance Target</span>
              <span class="badge-tag badge-info">Band C Threshold</span>
            </div>
            <span class="metric-value">{{ data.scenario_fixed_fuel.required_annual_distance_nm | number:'1.0-0' }} <small>nm</small></span>
            <span class="metric-sub">Minimum distance required to offset total fuel burn</span>
          </div>

          <div class="metric-card card-hero" [ngClass]="data.scenario_fixed_fuel.has_buffer ? 'card-success' : 'card-warning'">
            <div class="card-top-row">
              <span class="metric-label">Min Distance Left to Sail</span>
              <span class="badge-tag" [ngClass]="data.scenario_fixed_fuel.has_buffer ? 'badge-success' : 'badge-warning'">
                {{ data.scenario_fixed_fuel.min_required_distance_nm <= 0 ? 'Target Met' : (data.scenario_fixed_fuel.has_buffer ? 'Safe Ahead' : 'Deficit') }}
              </span>
            </div>
            <span class="metric-value hero-val">
              {{ data.scenario_fixed_fuel.min_required_distance_nm | number:'1.0-0' }} <small>nm</small>
            </span>
            <span class="metric-sub" *ngIf="data.scenario_fixed_fuel.min_required_distance_nm > 0; else modalDistMet">
              Buffer: <strong>{{ (data.scenario_fixed_fuel.distance_buffer_nm > 0 ? '+' : '') }}{{ data.scenario_fixed_fuel.distance_buffer_nm | number:'1.0-0' }} nm</strong> ahead of target pace
            </span>
            <ng-template #modalDistMet>
              <span class="metric-sub">
                <strong>Distance already achieved!</strong> Sailed {{ data.baseline_stats.distance_ytd | number:'1.0-0' }} nm vs {{ data.scenario_fixed_fuel.required_annual_distance_nm | number:'1.0-0' }} nm required
              </span>
            </ng-template>
          </div>
        </div>

        <div class="progress-section">
          <div class="progress-labels">
            <span>Distance Logged So Far: <strong>{{ data.baseline_stats.distance_ytd | number:'1.0-0' }} nm</strong></span>
            <span>Annual Target Distance: <strong>{{ data.scenario_fixed_fuel.required_annual_distance_nm | number:'1.0-0' }} nm</strong></span>
          </div>
          <div class="progress-bar-bg">
            <div class="progress-fill" [style.width.%]="getDistProgressWidth()" [class.over-limit]="!data.scenario_fixed_fuel.has_buffer"></div>
          </div>
        </div>

        <div class="action-alert" [ngClass]="data.scenario_fixed_fuel.has_buffer ? 'alert-success' : 'alert-warning'">
          <mat-icon>{{ data.scenario_fixed_fuel.has_buffer ? 'check_circle' : 'warning' }}</mat-icon>
          <p *ngIf="data.scenario_fixed_fuel.min_required_distance_nm > 0; else zeroDistSummary">
            <strong>Summary:</strong> At your current fuel burn rate, you only need to sail <strong>{{ data.scenario_fixed_fuel.min_required_distance_nm | number:'1.0-0' }} nm</strong> more to reach Band C target ({{ data.scenario_fixed_fuel.required_annual_distance_nm | number:'1.0-0' }} nm total). Because you are projected to sail <strong>{{ data.scenario_fixed_distance.projected_remaining_distance_nm | number:'1.0-0' }} nm</strong>, you have a healthy cushion of <strong>+{{ data.scenario_fixed_fuel.distance_buffer_nm | number:'1.0-0' }} nm</strong>.
          </p>
          <ng-template #zeroDistSummary>
            <p>
              <strong>Summary:</strong> Compliance target already achieved! At your current fuel burn rate (projected <strong>{{ data.scenario_fixed_fuel.annual_projected_fuel_mt | number:'1.0-1' }} MT</strong> for the year), you only needed <strong>{{ data.scenario_fixed_fuel.required_annual_distance_nm | number:'1.0-0' }} nm</strong>, but you have already logged <strong>{{ data.baseline_stats.distance_ytd | number:'1.0-0' }} nm</strong>. Any further distance you sail (projected <strong>+{{ data.scenario_fixed_distance.projected_remaining_distance_nm | number:'1.0-0' }} nm</strong>) will further improve your CII rating.
            </p>
          </ng-template>
        </div>
      </div>
    </div>
  </div>

  <div class="modal-footer" *ngIf="data">
    <div class="footer-info">
      <span>Year-To-Date Window: {{ data.period?.start_date | date:'mediumDate' }} &ndash; {{ data.period?.end_date | date:'mediumDate' }} ({{ data.period?.days_elapsed }} Days Elapsed &bull; {{ data.period?.days_remaining }} Remaining in {{ data.period?.reporting_year }})</span>
    </div>
    <button mat-button class="dismiss-btn" (click)="close()">Dismiss</button>
  </div>
</div>
